import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { openStoredDocument } from '@/lib/openDocument.js';
import { getImportQueue, getNamingChoices, nameImportedDocument, confirmImportedDocument, setImportAsideNotKept, keepImportedDocument, fileImportAsSignedApplication, getFilingAccounts, bulkImportAction, BULK_CHUNK } from '@/services/document-import.service.js';

// SCREEN 9, IMPORTED DOCUMENTS TO NAME (paperwork-1 D14, A8, R4; L4-2). The files the WorkDrive import brought into the
// documents home that a person has not named or confirmed yet. Each row says what it is called in WorkDrive, the folder it came
// from, what the import read it as and the policy it points at (by name and term), with one pick: what it is and whose.
// The pick files it through the server's one placement writer and confirms it (routes/document-import.routes.js); the row
// then shows the name TRACON stored for it. A placed import only needs a confirm. The server derives every suggestion; the
// page derives nothing, and an imported document is never sent until it is confirmed here (or on its deal's Paperwork tab,
// through the same confirm door). "Not a document we keep" (round 2, Q42) sets a file aside: it leaves both lists, nothing is
// deleted (the bytes and the WorkDrive file stay), and "Keep it after all" under "Set aside as not kept" undoes it.
// "Signed application" (round 2, Q38) files an unplaced import as the policy's signed application through the server's one
// application writer: it asks the policy it was signed for and the day it was signed. The server marks the outside
// applications (offer_first); for those it is offered first and chosen; on any other unplaced import it is the last choice.
// THE BULK TOOLS (David 2026-11-03): filter the To name list by what the import read each file as, the account it points at
// and the year its file name says (each a server field: reading.kind, account, name_year), select many, then file them on
// one account picked once, file them as old quotes kept on their account, or set them aside as not kept. The server's bulk
// door runs the same one-row doors row by row and answers each row; the page sends BULK_CHUNK at a time and reads the queue
// once at the end. A quote is filed and confirmed at once (quotes are never sent); anything else filed in bulk waits under
// To confirm. The list shows SHOW_STEP rows at a time (640 name pickers at once was the old page's weight).

const BUTTON = 'min-h-[36px] rounded-card border border-border px-3 text-xs text-text hover:border-accent disabled:opacity-50';
const PRIMARY = 'min-h-[36px] rounded-card bg-accent px-3 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50';
const SELECT = 'mt-1 w-full min-h-[36px] rounded-card border border-border bg-bg px-2 text-sm text-text';
const ACCOUNT = 'account:';
const ANOTHER = 'another';
const LEDGER_KINDS = new Set(['endorsement', 'cancellation']);
const SIGNED_APPLICATION = 'signed_application';
// Q19: a certificate names its holder; an endorsement may name a lienholder or loss payee (the loss-payee endorsement).
const HOLDER_KINDS = new Set(['certificate', 'endorsement']);
const SHOW_STEP = 100;
const ALL = '';
const NONE = 'none';
const AS_READ = 'as_read';
const OWN = 'own';
const size = (n) => (n == null ? null : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const errorWords = (e) => e?.body?.error || e?.message || 'the server did not answer';

function OpenButton({ row }) {
  return (
    <button type="button" onClick={() => openStoredDocument(row.id)} aria-label={`Open ${row.original_filename || row.name}`} className="inline-flex min-h-[36px] items-center gap-1 text-xs text-info hover:text-accent">
      Open <ExternalLink size={11} strokeWidth={1.5} />
    </button>
  );
}

function Provenance({ row }) {
  const r = row.reading;
  const pointing = row.suggested.policies;
  return (
    <div className="space-y-0.5 text-xs text-text-muted">
      <div>From WorkDrive folder <span className="break-all font-mono text-text">{row.folder_path || 'not recorded'}</span>{size(row.byte_size) ? ` · ${size(row.byte_size)}` : ''}</div>
      <div data-import-reading>
        {r ? `The import read it as ${r.kind_words}` : 'The import recorded no reading for it'}
        {pointing.length === 1 ? `, on ${pointing[0].label}` : pointing.length > 1 ? `, on one of ${pointing.length} policies: ${pointing.map((p) => p.label).join(' or ')}` : row.account && row.account.via !== 'filed' ? `, on the account ${row.account.name} (no policy found)` : ', and found no policy for it'}
        {row.suggested.interest ? `, naming ${row.suggested.interest.name}` : ''}.
      </div>
    </div>
  );
}

/** What the "On" select offers: the suggested and same-account policies, the account alone, and every other policy on request. */
function placeOptions(row) {
  const out = row.choices.policies.map((p) => ({ value: p.id, label: p.label }));
  const acct = row.suggested.account;
  if (acct) out.push({ value: `${ACCOUNT}${acct.id}`, label: `${acct.name}, the account itself (no policy)` });
  out.push({ value: ANOTHER, label: 'Another policy...' });
  return out;
}

function NamePicker({ row, classes, answers = [], policies, onFiled, confirmLabel = null, signedOnLatest = null }) {
  const offered = answers.some((a) => a.answer === row.offer_first) ? row.offer_first : null;
  const [klass, setKlass] = useState(offered || row.suggested.class || '');
  const [signedOn, setSignedOn] = useState('');
  const [place, setPlace] = useState(row.suggested.policies.length === 1 ? row.suggested.policies[0].id : '');
  const [other, setOther] = useState('');
  const [filter, setFilter] = useState('');
  const [choices, setChoices] = useState({ transactions: [], interests: [], loading: false, error: null });
  const [txn, setTxn] = useState(row.suggested.transaction?.live ? row.suggested.transaction.id : '');
  const [holder, setHolder] = useState(row.suggested.interest ? row.suggested.interest.id : '');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [replaceAsk, setReplaceAsk] = useState(null); // the server's "Does this replace <name>?" candidates, asked, never inferred

  const policyId = place === ANOTHER ? other : place.startsWith(ACCOUNT) ? '' : place;
  const accountId = place.startsWith(ACCOUNT) ? place.slice(ACCOUNT.length) : '';
  const policyLabel = useMemo(() => (policies.find((p) => p.id === policyId) || {}).label || null, [policies, policyId]);
  const placeLabel = accountId ? `${row.suggested.account?.name || 'the account'} (no policy)` : policyLabel;
  const isApplication = klass === SIGNED_APPLICATION;
  const classWord = isApplication ? 'the signed application' : (classes.find((c) => c.class === klass) || {}).word || null;
  const answerOptions = answers.map((a) => <option key={a.answer} value={a.answer} title={a.line}>{a.word}</option>);
  const filtered = useMemo(() => { const f = filter.trim().toLowerCase(); return (f ? policies.filter((p) => p.label.toLowerCase().includes(f)) : policies).slice(0, 50); }, [policies, filter]);

  useEffect(() => {
    if (!policyId || !(LEDGER_KINDS.has(klass) || HOLDER_KINDS.has(klass))) { setChoices({ transactions: [], interests: [], loading: false, error: null }); return undefined; }
    let live = true;
    setChoices((c) => ({ ...c, loading: true, error: null }));
    getNamingChoices(policyId, klass).then((r) => { if (live) setChoices({ transactions: r.transactions || [], interests: r.interests || [], loading: false, error: null }); })
      .catch((e) => { if (live) setChoices({ transactions: [], interests: [], loading: false, error: errorWords(e) }); });
    return () => { live = false; };
  }, [policyId, klass]);

  useEffect(() => { setReplaceAsk(null); }, [klass, place, other, txn, holder]);
  // Round 2 review (the Chrome pass): a signing day after the server's latest (signed_on_latest, today Eastern) is not an answer.
  const signedOnLate = !!signedOn && !!signedOnLatest && signedOn > signedOnLatest;
  const ready = isApplication ? !!policyId && !!signedOn && !signedOnLate && !busy : !!klass && (!!policyId || !!accountId) && !busy;
  const missing = isApplication
    ? (!policyId ? 'Choose the policy it was signed for' : signedOnLate ? 'A signing day cannot be after today' : 'Say the day it was signed')
    : !klass && !(policyId || accountId) ? 'Choose what it is and whose to file it' : !klass ? 'Choose what it is to file it' : 'Choose whose it is to file it';
  const fileApplication = async () => {
    setBusy(true); setRefusal(null);
    try {
      const r = await fileImportAsSignedApplication(row.id, { policyId, signedOn });
      toast.success(`Filed as the signed application of ${r.policy?.label || policyLabel}, signed ${r.application?.signed_on || signedOn}.`);
      onFiled(row, { name: `Signed application of ${r.policy?.label || policyLabel}, signed ${r.application?.signed_on || signedOn}` }, { asApplication: true });
    } catch (e) { setRefusal(errorWords(e)); } finally { setBusy(false); }
  };
  const file = async (replacesDocumentId) => {
    setBusy(true); setRefusal(null);
    try {
      const statement = { klass, ...(accountId ? { accountId } : { policyId }), ...(LEDGER_KINDS.has(klass) && txn ? { transactionId: txn } : {}), ...(holder && (klass === 'certificate' || (klass === 'endorsement' && choices.interests.some((i) => i.id === holder && i.is_loss_payee))) ? { interestId: holder } : {}),
        ...(replacesDocumentId ? { replacesDocumentId } : {}) };
      const r = await nameImportedDocument(row.id, statement);
      toast.success(`Filed as ${r.document?.name || 'named'} and confirmed.`);
      onFiled(row, r.document, { replaced: r.replaced ? (replaceAsk || []).find((c) => c.id === r.replaced) : null });
    } catch (e) {
      if (e?.body?.code === 'REPLACES_UNANSWERED') { setReplaceAsk(e.body.candidates || []); return; }
      setRefusal(errorWords(e));
      if (e?.body?.placed) onFiled(row, e.body.document, { unconfirmed: true });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2" data-name-picker={row.id}>
      <label className="block text-xs text-text-muted">
        It is
        <select value={klass} onChange={(e) => setKlass(e.target.value)} className={SELECT} data-pick-class>
          <option value="">Choose what it is</option>
          {offered ? answerOptions : null}
          {classes.map((c) => <option key={c.class} value={c.class}>{c.word}</option>)}
          {offered ? null : answerOptions}
        </select>
      </label>
      <label className="block text-xs text-text-muted">
        On
        <select value={place} onChange={(e) => setPlace(e.target.value)} className={SELECT} data-pick-place>
          <option value="">Choose whose it is</option>
          {placeOptions(row).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      {place === ANOTHER ? (
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-muted">
            Find the policy (account name, policy number or term)
            <input value={filter} onChange={(e) => setFilter(e.target.value)} className={SELECT} data-pick-filter />
          </label>
          <select value={other} onChange={(e) => setOther(e.target.value)} className={SELECT} data-pick-other>
            <option value="">{filtered.length ? `Choose the policy (${filtered.length} found${filter.trim() ? ` for "${filter.trim()}"` : ''})` : 'No policy matches those words'}</option>
            {filtered.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
      ) : null}
      {isApplication ? (
        <label className="block text-xs text-text-muted sm:col-span-2">
          Signed on (the date on the application)
          <input type="date" required max={signedOnLatest || undefined} value={signedOn} onChange={(e) => setSignedOn(e.target.value)} className={SELECT} data-signed-on={row.id} aria-label={`The day ${row.original_filename || row.name} was signed`} />
          {accountId ? <span className="mt-1 block text-warning">A signed application belongs to the policy it was signed for; choose the policy, not the account.</span> : null}
        </label>
      ) : null}
      {LEDGER_KINDS.has(klass) && policyId ? (
        <label className="block text-xs text-text-muted sm:col-span-2">
          The ledger row it belongs to
          <select value={txn} onChange={(e) => setTxn(e.target.value)} className={SELECT} data-pick-transaction>
            <option value="">Not on the ledger yet (file it unattached)</option>
            {choices.transactions.map((t) => <option key={t.id} value={t.id}>{`${t.type === 'cancellation' ? 'Cancellation' : 'Endorsement'} effective ${t.effective_date || 'date not recorded'}${t.description ? `: ${t.description}` : ''}`}</option>)}
          </select>
        </label>
      ) : null}
      {klass === 'certificate' && policyId ? (
        <label className="block text-xs text-text-muted sm:col-span-2">
          The holder it names
          <select value={holder} onChange={(e) => setHolder(e.target.value)} className={SELECT} data-pick-holder>
            <option value="">Nobody on the interests list</option>
            {choices.interests.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </label>
      ) : null}
      {klass === 'endorsement' && policyId && choices.interests.some((i) => i.is_loss_payee) ? (
        <label className="block text-xs text-text-muted sm:col-span-2">
          Does it name a lienholder or loss payee?
          <select value={holder} onChange={(e) => setHolder(e.target.value)} className={SELECT} data-pick-loss-payee>
            <option value="">It names none of them</option>
            {choices.interests.filter((i) => i.is_loss_payee).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </label>
      ) : null}
      {choices.error ? <p className="text-xs text-warning sm:col-span-2">Could not read the ledger rows and holders for that policy: {choices.error}</p> : null}
      {replaceAsk ? (
        <div className="space-y-2 rounded-card border border-warning p-2 text-xs text-text sm:col-span-2" data-import-replace>
          <p>{placeLabel} already holds {replaceAsk.length === 1 ? 'a' : replaceAsk.length} live {classWord}{replaceAsk.length === 1 ? '' : 's'}. Does this replace {replaceAsk.length === 1 ? 'it' : 'one of them'}? Nothing is filed until you say.</p>
          <div className="flex flex-wrap gap-2">
            {replaceAsk.map((c) => (
              <span key={c.id} className="inline-flex flex-wrap items-center gap-1">
                <button type="button" disabled={busy} onClick={() => file(c.id)} className={BUTTON} data-replace-yes={c.id}>{`Yes, it replaces ${c.name}${c.original_filename ? ` (${c.original_filename})` : ''}`}</button>
                <OpenButton row={{ id: c.id, name: c.name, original_filename: c.original_filename }} />
              </span>
            ))}
            <button type="button" disabled={busy} onClick={() => file('none')} className={BUTTON} data-replace-no>No, keep both</button>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
        {isApplication ? (
          <button type="button" disabled={!ready} onClick={fileApplication} className={PRIMARY} data-file-import={row.id} data-file-application={row.id}>
            {ready && policyLabel ? `File as the signed application of ${policyLabel}, signed ${signedOn}` : missing}
          </button>
        ) : (
          <button type="button" disabled={!ready || !!replaceAsk} onClick={() => file()} className={PRIMARY} data-file-import={row.id}>
            {ready && classWord && placeLabel ? (confirmLabel || `File as ${classWord} on ${placeLabel}`) : missing}
          </button>
        )}
        {refusal ? <span className="text-xs text-danger" data-import-refusal>{refusal}</span> : null}
      </div>
    </div>
  );
}

const fileWord = (row) => row.original_filename || row.name || 'this file';
const when = (iso) => (iso ? String(iso).slice(0, 10) : 'a day not recorded');

/** "Not a document we keep": an optional reason, then the server's answer (the row leaves the queue on the re-read). */
function NotKeptControl({ row, onAnswered }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const setAside = async () => {
    setBusy(true); setRefusal(null);
    try {
      await setImportAsideNotKept(row.id, reason.trim() || null);
      toast.success(`Set aside: ${fileWord(row)} is not a document we keep. Nothing was deleted; "Keep it after all" brings it back.`);
      onAnswered();
    } catch (e) { setRefusal(errorWords(e)); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className={BUTTON} data-not-kept-open={row.id}>Not a document we keep</button>;
  return (
    <div className="mt-2 w-full space-y-2 rounded-card border border-border p-2 text-xs" data-not-kept-form={row.id}>
      <label className="block text-text-muted">
        Why, if you want to say (only you see it)
        <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} className={SELECT} data-not-kept-reason />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={setAside} className={PRIMARY} data-not-kept-confirm={row.id}>{busy ? 'Setting it aside...' : `Set ${fileWord(row)} aside: not ours to keep`}</button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setRefusal(null); }} className={BUTTON}>Keep it on the list</button>
        {refusal ? <span className="text-danger" data-not-kept-refusal>{refusal}</span> : null}
      </div>
    </div>
  );
}

function NotKeptRow({ row, onKept }) {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const keep = async () => {
    setBusy(true); setRefusal(null);
    try {
      await keepImportedDocument(row.id);
      toast.success(`Kept after all: ${fileWord(row)} is back on the list to name.`);
      onKept();
    } catch (e) { setRefusal(errorWords(e)); } finally { setBusy(false); }
  };
  const n = row.not_kept || {};
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs" data-not-kept-row={row.id}>
      <span className="text-sm font-medium text-text">{fileWord(row)}</span>
      <span className="text-text-muted">set aside {when(n.at)}{n.by ? ` by ${n.by}` : ''}{n.reason ? `: "${n.reason}"` : ', no reason given'}</span>
      {row.folder_path ? <span className="break-all font-mono text-text-muted">{row.folder_path}</span> : null}
      <OpenButton row={row} />
      <button type="button" disabled={busy} onClick={keep} className={BUTTON} data-keep-after-all={row.id}>{busy ? 'Keeping it...' : `Keep ${fileWord(row)} after all`}</button>
      {refusal ? <span className="text-danger">{refusal}</span> : null}
    </li>
  );
}

function ToNameRow({ row, classes, answers, policies, onFiled, onAnswered, signedOnLatest, selected, onSelect }) {
  // "Signed application" only for an import not yet filed on anything (the server refuses a placed one, IMPORT_PLACED).
  const fileable = row.class === 'unclassified' && !row.policy;
  return (
    <li className="py-3" data-import-row={row.id}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <label className="inline-flex min-h-[36px] items-center gap-2 text-sm font-medium text-text">
          <input type="checkbox" checked={selected} onChange={(e) => onSelect(row.id, e.target.checked)} className="h-4 w-4 accent-accent" data-import-select={row.id} aria-label={`Select ${row.original_filename || row.name}`} />
          <span className="break-all">{row.original_filename || row.name}</span>
        </label>
        <OpenButton row={row} />
      </div>
      <Provenance row={row} />
      <NamePicker row={row} classes={classes} answers={fileable ? answers : []} policies={policies} onFiled={onFiled} signedOnLatest={signedOnLatest} />
      <div className="mt-2"><NotKeptControl row={row} onAnswered={onAnswered} /></div>
    </li>
  );
}

function ToConfirmRow({ row, classes, policies, onFiled, onAnswered, signedOnLatest }) {
  const [busy, setBusy] = useState(false);
  const [again, setAgain] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const onAccount = !row.policy && row.account?.via === 'filed' ? row.account : null; // filed on the account by the bulk tools
  const where = row.policy ? row.policy.label : onAccount ? `${onAccount.name}, the account itself` : 'its policy';
  const confirm = async () => {
    setBusy(true); setRefusal(null);
    try {
      const r = await confirmImportedDocument(row.id);
      toast.success(`Confirmed as ${onAccount ? 'the account\'s' : 'this policy\'s'} (${where}): ${r.document?.name || row.name}.`);
      onFiled(row, r.document, { confirmedOnly: onAccount ? 'account' : 'policy' });
    } catch (e) { setRefusal(errorWords(e)); } finally { setBusy(false); }
  };
  return (
    <li className="py-3" data-import-row={row.id}>
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-sm font-medium text-text">{row.name}</span>
        <span className="text-xs text-text-muted">filed from {row.original_filename || 'a file with no name'}</span>
        <OpenButton row={row} />
      </div>
      <Provenance row={row} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={confirm} className={PRIMARY} data-confirm-import={row.id}>{onAccount ? `Confirm it is ${onAccount.name}'s ${row.class_word || 'document'} (the account itself)` : `Confirm it is this policy's ${row.class_word || 'document'}: ${where}`}</button>
        <button type="button" disabled={busy} onClick={() => setAgain((v) => !v)} className={BUTTON}>{again ? 'Keep it as it is' : 'No, it is something else'}</button>
        <NotKeptControl row={row} onAnswered={onAnswered} />
        {refusal ? <span className="text-xs text-danger">{refusal}</span> : null}
      </div>
      {again ? <NamePicker row={row} classes={classes} policies={policies} onFiled={onFiled} signedOnLatest={signedOnLatest} /> : null}
    </li>
  );
}

function FiledRow({ entry }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 py-2 text-xs" data-import-filed={entry.id}>
      <CheckCircle2 size={14} strokeWidth={1.75} className={entry.unconfirmed ? 'text-warning' : 'text-success'} />
      <span className="font-medium text-text">{entry.name}</span>
      <span className="text-text-muted">from {entry.original_filename || 'a file with no name'}</span>
      <span className={entry.unconfirmed ? 'text-warning' : 'text-text-muted'}>{entry.unconfirmed ? 'filed, NOT confirmed: confirm it below' : entry.asApplication ? 'filed as the signed application (the Paperwork tab counts it)' : entry.confirmedOnly === 'account' ? 'confirmed as the account\'s' : entry.confirmedOnly ? 'confirmed as its policy\'s' : 'filed and confirmed'}</span>
      {entry.replaced ? <span className="text-text-muted">replaces the earlier {entry.replaced.name}{entry.replaced.original_filename ? ` (${entry.replaced.original_filename})` : ''}, now marked replaced</span> : null}
      <OpenButton row={{ id: entry.id, name: entry.name }} />
    </li>
  );
}

/** The choices a filter offers, each with how many of the To name rows it matches (server fields only). */
function tally(rows, keyOf, labelOf) {
  const m = new Map();
  for (const r of rows) { const k = keyOf(r); const e = m.get(k) || { value: k, label: labelOf(r), count: 0 }; e.count += 1; m.set(k, e); }
  return [...m.values()];
}
const readingKey = (r) => (r.reading ? r.reading.kind : NONE);
const accountKey = (r) => (r.account ? String(r.account.id) : NONE);
const yearKey = (r) => (r.name_year ? String(r.name_year) : NONE);
const matches = (r, f) => (f.kind === ALL || readingKey(r) === f.kind) && (f.account === ALL || accountKey(r) === f.account) && (f.year === ALL || yearKey(r) === f.year);
const noneLast = (a, b) => (a.value === NONE) - (b.value === NONE);

function FilterSelect({ label, value, onChange, options, total, attr }) {
  return (
    <label className="block text-xs text-text-muted">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className={SELECT} {...{ [attr]: '' }}>
        <option value={ALL}>{`All (${total})`}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{`${o.label} (${o.count})`}</option>)}
      </select>
    </label>
  );
}

/** Filter the To name list by what the import read, the account it points at and the year in the file name. */
function BulkFilters({ rows, filter, setFilter }) {
  const kinds = useMemo(() => tally(rows, readingKey, (r) => (r.reading ? r.reading.kind_words : 'no reading recorded')).sort((a, b) => noneLast(a, b) || b.count - a.count), [rows]);
  const accounts = useMemo(() => tally(rows, accountKey, (r) => (r.account ? `${r.account.name}${r.account.merged ? ' (merged into another account)' : ''}` : 'No account found')).sort((a, b) => noneLast(a, b) || a.label.localeCompare(b.label)), [rows]);
  const years = useMemo(() => tally(rows, yearKey, (r) => (r.name_year ? String(r.name_year) : 'No year in the name')).sort((a, b) => noneLast(a, b) || b.value.localeCompare(a.value)), [rows]);
  const set = (k) => (v) => setFilter((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid gap-2 sm:grid-cols-3" data-bulk-filters>
      <FilterSelect label="What the import read it as" value={filter.kind} onChange={set('kind')} options={kinds} total={rows.length} attr="data-filter-kind" />
      <FilterSelect label="The account it points at" value={filter.account} onChange={set('account')} options={accounts} total={rows.length} attr="data-filter-account" />
      <FilterSelect label="The year in its file name" value={filter.year} onChange={set('year')} options={years} total={rows.length} attr="data-filter-year" />
    </div>
  );
}

/** "On": each one's own account, or one account picked once from the agency's live accounts (read when first asked for). */
function AccountPick({ mode, setMode, picked, setPicked, disabled }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [find, setFind] = useState('');
  useEffect(() => {
    if (mode !== 'pick' || accounts) return undefined;
    let live = true;
    setError(null);
    getFilingAccounts().then((r) => { if (live) setAccounts(r.accounts || []); }).catch((e) => { if (live) setError(errorWords(e)); });
    return () => { live = false; };
  }, [mode, accounts, attempt]);
  const matched = useMemo(() => { const f = find.trim().toLowerCase(); return (accounts || []).filter((a) => !f || a.label.toLowerCase().includes(f)); }, [accounts, find]);
  // The picked account stays in the list while other words are typed: the select never shows a placeholder over a live pick.
  const found = useMemo(() => { const first = matched.slice(0, 50); return picked && !first.some((a) => a.id === picked.id) ? [picked, ...first] : first; }, [matched, picked]);
  return (
    <div className="space-y-1">
      <label className="block text-xs text-text-muted">
        On
        <select value={mode} disabled={disabled} onChange={(e) => { setMode(e.target.value); setPicked(null); }} className={SELECT} data-bulk-account-mode>
          <option value={OWN}>Each one&apos;s own account (the one the import found)</option>
          <option value="pick">One account I choose</option>
        </select>
      </label>
      {mode === 'pick' ? (
        error ? (
          <p className="flex flex-wrap items-center gap-2 text-xs text-danger">
            <span>Could not read the accounts: {error}</span>
            <button type="button" onClick={() => setAttempt((n) => n + 1)} className={BUTTON} data-bulk-accounts-again>Read the accounts again</button>
          </p>
        )
          : accounts === null ? <p className="text-xs text-text-muted">Reading the accounts&hellip;</p>
            : (
              <>
                <input value={find} disabled={disabled} onChange={(e) => setFind(e.target.value)} placeholder="Find the account by name" className={SELECT} data-bulk-account-find aria-label="Find the account by name" />
                <select value={picked ? picked.id : ''} disabled={disabled} onChange={(e) => setPicked(accounts.find((a) => String(a.id) === e.target.value) || null)} className={SELECT} data-bulk-account>
                  <option value="">{matched.length ? `Choose the account (${matched.length} found${matched.length > 50 ? ', the first 50 listed' : ''})` : 'No account matches those words'}</option>
                  {found.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </>
            )
      ) : null}
    </div>
  );
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The bulk actions over the selected rows: sends BULK_CHUNK at a time, then hands every row's answer to onDone. */
function BulkActions({ selected, byId, classes, onDone }) {
  const [klass, setKlass] = useState(AS_READ);
  const [mode, setMode] = useState(OWN);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState('');
  const [asideOpen, setAsideOpen] = useState(false);
  const [running, setRunning] = useState(null);
  const n = selected.length;
  const rows = selected.map((id) => byId.get(id)).filter(Boolean);
  const where = mode === 'pick' ? (picked ? picked.name : null) : 'each one\'s own account';
  const klassWord = klass === AS_READ ? 'what the import read each one as' : (classes.find((c) => c.class === klass) || {}).word;
  // A preview of what the server will refuse, from the same server fields it decides by (account, suggested.class), counted
  // once per row and per button: the File button reads the kind select; the old quotes button never needs a kind.
  const noAccount = (r) => mode === OWN && (!r.account || r.account.merged);
  const fileRefused = rows.filter((r) => noAccount(r) || (klass === AS_READ && !r.suggested.class)).length;
  const quoteRefused = rows.filter(noAccount).length;
  const busy = !!running;
  const run = async (label, body) => {
    const results = [];
    let failure = null;
    for (let i = 0; i < selected.length; i += BULK_CHUNK) {
      const ids = selected.slice(i, i + BULK_CHUNK);
      setRunning({ label, done: i, total: selected.length });
      try { results.push(...(await bulkImportAction({ ...body, ids })).results); }
      catch (e) { failure = { words: errorWords(e), status: e?.status || 0, ids: selected.slice(i) }; break; }
    }
    setRunning(null);
    onDone({ action: body.action, klass: body.klass || null, where, results, failure });
  };
  const file = (k) => run(`Filing ${plural(n, 'document', 'documents')}`, { action: 'file', klass: k, account: mode === 'pick' ? String(picked.id) : OWN });
  const fileReady = n > 0 && !busy && (mode === OWN || !!picked);
  return (
    <div className="space-y-2" data-bulk-actions>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-text-muted">
          They are
          <select value={klass} disabled={busy} onChange={(e) => setKlass(e.target.value)} className={SELECT} data-bulk-class>
            <option value={AS_READ}>What the import read each one as</option>
            {classes.map((c) => <option key={c.class} value={c.class}>{c.word}</option>)}
          </select>
        </label>
        <AccountPick mode={mode} setMode={setMode} picked={picked} setPicked={setPicked} disabled={busy} />
      </div>
      {n && (fileRefused || quoteRefused) ? (
        <p className="text-xs text-warning" data-bulk-preview>
          {`Of these ${n}, File refuses ${fileRefused} and the old quotes button refuses ${quoteRefused}: ${mode === OWN ? 'no account the import found (or one merged since)' : ''}${mode === OWN && klass === AS_READ ? ', or ' : ''}${klass === AS_READ ? 'no kind the import read (File only)' : ''}. Refused ones stay on the list; choose one account${klass === AS_READ ? ' or one kind' : ''} to file them too.`}
        </p>
      ) : null}
      <p className="text-xs text-text-muted">A quote is filed and confirmed at once (a quote is never sent to anyone). Anything else filed here waits under To confirm until you confirm it.</p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!fileReady} onClick={() => file(klass)} className={PRIMARY} data-bulk-file>
          {!n ? 'Select documents to file them' : !where ? 'Choose the account to file them on' : `File ${plural(n, 'document', 'documents')} as ${klassWord} on ${where}`}
        </button>
        <button type="button" disabled={!fileReady} onClick={() => file('quote')} className={BUTTON} data-bulk-quote>
          {!n ? 'Old quotes, keep on the account' : !where ? 'Choose the account to keep them on' : `File ${plural(n, 'document', 'documents')} as old quotes, kept on ${where}`}
        </button>
        <button type="button" disabled={!n || busy} onClick={() => setAsideOpen((v) => !v)} className={BUTTON} data-bulk-not-kept-open>{n ? `Not documents we keep (${n})` : 'Not documents we keep'}</button>
      </div>
      {asideOpen && n ? (
        <div className="space-y-2 rounded-card border border-border p-2 text-xs" data-bulk-not-kept-form>
          <label className="block text-text-muted">
            Why, if you want to say (only you see it; the same reason for every one)
            <input value={reason} maxLength={500} disabled={busy} onChange={(e) => setReason(e.target.value)} className={SELECT} data-bulk-not-kept-reason />
          </label>
          <button type="button" disabled={busy} onClick={() => run(`Setting ${plural(n, 'document', 'documents')} aside`, { action: 'not_kept', ...(reason.trim() ? { reason: reason.trim() } : {}) })} className={PRIMARY} data-bulk-not-kept>
            {`Set ${plural(n, 'document', 'documents')} aside: not ours to keep`}
          </button>
        </div>
      ) : null}
      {running ? <p className="text-xs text-text" data-bulk-progress>{`${running.label}: ${running.done} of ${running.total} done...`}</p> : null}
    </div>
  );
}

/** What a bulk action did: the count done, those waiting for a confirm, and every refused row with its reason. */
function BulkResult({ result, byName, onReadAgain }) {
  const ok = result.results.filter((r) => r.ok);
  const refused = result.results.filter((r) => !r.ok);
  const confirmed = ok.filter((r) => r.confirmed).length;
  const fileOf = (id) => byName.get(id) || id;
  const head = result.action === 'not_kept'
    ? `Set ${plural(ok.length, 'document', 'documents')} aside as not kept. Nothing was deleted; each can be kept after all below.`
    : `Filed ${plural(ok.length, 'document', 'documents')} on ${result.where}: ${confirmed} filed and confirmed${ok.length - confirmed ? `, ${ok.length - confirmed} waiting under To confirm` : ''}.`;
  return (
    <div className="space-y-1 rounded-card border border-border bg-surface p-3 text-xs" data-bulk-result>
      <p className="text-sm text-text">{head}</p>
      {refused.length ? <p className="text-warning">{`${plural(refused.length, 'was', 'were')} refused and stay${refused.length === 1 ? 's' : ''} on the list:`}</p> : null}
      {refused.length ? (
        <ul className="space-y-0.5">
          {refused.map((r) => <li key={r.id} data-bulk-refused={r.id}><span className="font-medium text-text">{fileOf(r.id)}</span>: {r.error || 'refused'}{r.placed ? ' (filed, but NOT confirmed: confirm it under To confirm)' : ''}</li>)}
        </ul>
      ) : null}
      {result.failure ? (
        <p className="flex flex-wrap items-center gap-2 text-danger" data-bulk-failure>
          <span>{result.failure.status >= 400 && result.failure.status < 500
            ? `Refused for the last ${result.failure.ids.length}: ${result.failure.words}. None of those was filed.`
            : `No answer for the last ${result.failure.ids.length} (${result.failure.words}). The server may still be filing some of them: read the list again in a minute, and any that were filed will have left it.`}</span>
          {result.failure.status >= 400 && result.failure.status < 500 ? null : <button type="button" onClick={onReadAgain} className={BUTTON} data-bulk-read-again>Read the list again</button>}
        </p>
      ) : null}
    </div>
  );
}

export default function ImportedDocuments() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filed, setFiled] = useState([]);
  const [filter, setFilterState] = useState({ kind: ALL, account: ALL, year: ALL });
  const [selected, setSelected] = useState(() => new Set());
  const [shown, setShown] = useState(SHOW_STEP);
  const [bulk, setBulk] = useState(null);
  const [bulkRound, setBulkRound] = useState(0); // each finished action starts the bar over: no kind or account carries to the next batch
  // A changed filter starts the selection over: what is selected is always what the filters show.
  const setFilter = useCallback((f) => { setFilterState(f); setSelected(new Set()); setShown(SHOW_STEP); }, []);
  const refresh = useCallback(async () => {
    try { setData(await getImportQueue()); setError(''); }
    catch (e) { setError(errorWords(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const onFiled = useCallback(async (row, stored, { unconfirmed = false, replaced = null, confirmedOnly = false, asApplication = false } = {}) => {
    setFiled((f) => [{ id: row.id, name: stored?.name || row.name, original_filename: row.original_filename, unconfirmed, replaced, confirmedOnly, asApplication }, ...f.filter((x) => x.id !== row.id)]);
    await refresh();
  }, [refresh]);

  const toName = data ? data.to_name : [];
  const matching = useMemo(() => toName.filter((r) => matches(r, filter)), [toName, filter]);
  const byId = useMemo(() => new Map(toName.map((r) => [r.id, r])), [toName]);
  // A filter whose choice has no row left (the batch just filed every one of them) goes back to All, so the select never
  // says All while the list still filters on the emptied choice.
  useEffect(() => {
    if (!data) return;
    const has = (keyOf, v) => v === ALL || data.to_name.some((r) => keyOf(r) === v);
    setFilterState((f) => (has(readingKey, f.kind) && has(accountKey, f.account) && has(yearKey, f.year) ? f
      : { kind: has(readingKey, f.kind) ? f.kind : ALL, account: has(accountKey, f.account) ? f.account : ALL, year: has(yearKey, f.year) ? f.year : ALL }));
  }, [data]);
  const [byName, setByName] = useState(() => new Map());
  const select = useCallback((id, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; }), []);
  const selectedIds = useMemo(() => matching.filter((r) => selected.has(r.id)).map((r) => r.id), [matching, selected]);
  const onBulkDone = useCallback(async (result) => {
    setByName(new Map(result.results.map((r) => [r.id, byId.get(r.id)?.original_filename || byId.get(r.id)?.name || r.name || r.id])));
    setBulk(result);
    setBulkRound((n) => n + 1);
    const ok = result.results.filter((r) => r.ok).length;
    const refused = result.results.length - ok;
    if (ok) toast.success(result.action === 'not_kept' ? `Set ${ok} aside as not kept.` : `Filed ${ok} on ${result.where}.`);
    if (refused) toast.info(`${refused} refused and still on the list; each one's reason is listed above the list.`);
    if (result.failure) toast.info(result.failure.status >= 400 && result.failure.status < 500 ? `Refused for the last ${result.failure.ids.length}: ${result.failure.words}` : `No answer for the last ${result.failure.ids.length}; read the list again in a minute.`);
    setSelected((s) => { const n = new Set(s); for (const r of result.results) if (r.ok) n.delete(r.id); return n; });
    await refresh();
  }, [byId, refresh]);

  const left = data ? data.counts.to_name + data.counts.to_confirm : null;
  return (
    <div className="p-4" data-imported-documents>
      <h2 className="text-xl font-semibold text-text">Imported documents to name</h2>
      <p className="mt-1 text-sm text-text-muted">
        Files brought over from WorkDrive. Say what each one is and whose; it is filed under the name TRACON gives it, and an imported document is never sent to anyone until it is named and confirmed here.
      </p>
      {error ? (
        <p className="mt-4 flex flex-wrap items-center gap-2 text-sm text-danger">
          <span>Could not read the imported documents: {error}</span>
          <button type="button" onClick={refresh} className={BUTTON}>Read them again</button>
        </p>
      ) : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Reading the imported documents&hellip;</p> : null}
      {data ? (
        <>
          <p className="mt-4 font-mono text-sm text-text" data-import-counts>{data.counts.to_name} to name · {data.counts.to_confirm} to confirm{data.counts.not_kept ? ` · ${data.counts.not_kept} set aside as not kept` : ''}</p>
          {filed.length ? (
            <section className="mt-3">
              <h3 className="text-sm font-semibold text-text">Filed just now ({filed.length})</h3>
              <ul className="mt-1 divide-y divide-border rounded-card border border-border bg-surface px-3">{filed.map((f) => <FiledRow key={f.id} entry={f} />)}</ul>
            </section>
          ) : null}
          {left === 0 ? (
            <p className="mt-4 text-sm text-text-muted" data-import-empty>Nothing left to name: 0 imported documents are waiting. {data.counts.not_kept ? `Every other one is named and confirmed; ${data.counts.not_kept} ${data.counts.not_kept === 1 ? 'is' : 'are'} set aside as not kept, below.` : 'Every one is named and confirmed.'}</p>
          ) : null}
          {bulk ? <div className="mt-3"><BulkResult result={bulk} byName={byName} onReadAgain={refresh} /></div> : null}
          {data.to_name.length ? (
            <section className="mt-4">
              <h3 className="text-sm font-semibold text-text">To name ({data.to_name.length})</h3>
              <div className="mt-2 space-y-3 rounded-card border border-border bg-surface p-3" data-bulk-bar>
                <BulkFilters rows={data.to_name} filter={filter} setFilter={setFilter} />
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-text" data-bulk-counts>{`${matching.length} shown by the filters · ${selectedIds.length} selected`}</span>
                  <button type="button" disabled={!matching.length || selectedIds.length === matching.length} onClick={() => setSelected(new Set(matching.map((r) => r.id)))} className={BUTTON} data-bulk-select-all>{`Select all ${matching.length} shown`}</button>
                  <button type="button" disabled={!selectedIds.length} onClick={() => setSelected(new Set())} className={BUTTON} data-bulk-clear>Clear the selection</button>
                </div>
                <BulkActions key={bulkRound} selected={selectedIds} byId={byId} classes={data.classes} onDone={onBulkDone} />
              </div>
              {matching.length ? (
                <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                  {matching.slice(0, shown).map((row) => <ToNameRow key={row.id} row={row} classes={data.classes} answers={data.answers || []} policies={data.policies} onFiled={onFiled} onAnswered={refresh} signedOnLatest={data.signed_on_latest} selected={selected.has(row.id)} onSelect={select} />)}
                </ul>
              ) : <p className="mt-2 text-sm text-text-muted" data-bulk-none>No document to name matches those filters.</p>}
              {matching.length > shown ? (
                <button type="button" onClick={() => setShown((v) => v + SHOW_STEP)} className={`${BUTTON} mt-2`} data-show-more>{`Show ${Math.min(SHOW_STEP, matching.length - shown)} more (${matching.length - shown} not shown yet; Select all takes them too)`}</button>
              ) : null}
            </section>
          ) : null}
          {data.to_confirm.length ? (
            <section className="mt-6">
              <h3 className="text-sm font-semibold text-text">To confirm ({data.to_confirm.length})</h3>
              <p className="text-xs text-text-muted">Filed but not yet confirmed: the import placed them on one policy, or you filed them in bulk on an account. Confirm each one, or say what it is instead.</p>
              <ul className="mt-1 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {data.to_confirm.map((row) => <ToConfirmRow key={row.id} row={row} classes={data.classes} policies={data.policies} onFiled={onFiled} onAnswered={refresh} signedOnLatest={data.signed_on_latest} />)}
              </ul>
            </section>
          ) : null}
          {(data.not_kept || []).length ? (
            <section className="mt-6" data-not-kept-section>
              <h3 className="text-sm font-semibold text-text">Set aside as not kept ({data.not_kept.length})</h3>
              <p className="text-xs text-text-muted">Files you said are not documents we keep. They are in no list and are never sent; nothing was deleted. Keep one after all to bring it back to name.</p>
              <ul className="mt-1 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {data.not_kept.map((row) => <NotKeptRow key={row.id} row={row} onKept={refresh} />)}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
